import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

type LegalDocumentType = 'terms' | 'privacy' | 'risk';
type LegalContentLanguage = 'hk' | 'cn' | 'en' | 'ug';

type LegalDocumentPageProps = {
  documentType: LegalDocumentType;
};

const DOCUMENT_FILES: Record<LegalDocumentType, string> = {
  terms: 'user-agreement.html',
  privacy: 'privacy-policy.html',
  risk: 'risk-disclosure.html',
};

const DOCUMENT_ROUTES: Record<LegalDocumentType, string> = {
  terms: '/terms',
  privacy: '/privacy',
  risk: '/risk-disclosure',
};

const PAGE_COPY: Record<LegalContentLanguage, {
  eyebrow: string;
  heading: string;
  subtitle: string;
  terms: string;
  privacy: string;
  risk: string;
  loading: string;
  error: string;
}> = {
  hk: {
    eyebrow: '法律文件',
    heading: '金利寶環球法律文件',
    subtitle: '請仔細閱讀適用於本網站、開戶及相關服務的法律文件。',
    terms: '用戶使用協議',
    privacy: '隱私政策',
    risk: '貴金屬風險披露',
    loading: '正在載入文件…',
    error: '暫時無法載入文件，請稍後再試。',
  },
  cn: {
    eyebrow: '法律文件',
    heading: '金利宝环球法律文件',
    subtitle: '请仔细阅读适用于本网站、开户注册及相关服务的法律文件。',
    terms: '用户使用协议',
    privacy: '隐私政策',
    risk: '贵金属风险披露',
    loading: '正在加载文件…',
    error: '暂时无法加载文件，请稍后再试。',
  },
  en: {
    eyebrow: 'Legal Documents',
    heading: 'Gold Label Global Legal Documents',
    subtitle: 'Please review the legal documents applicable to this website, account opening and related services.',
    terms: 'User Agreement',
    privacy: 'Privacy Policy',
    risk: 'Precious Metals Risk Disclosure',
    loading: 'Loading document…',
    error: 'The document is temporarily unavailable. Please try again later.',
  },
  ug: {
    eyebrow: 'قانۇنىي ھۆججەتلەر',
    heading: 'Gold Label Global قانۇنىي ھۆججەتلىرى',
    subtitle: 'بۇ تور بېكىتى، ھېسابات ئېچىش ۋە مۇناسىۋەتلىك مۇلازىمەتلەرگە ماس كېلىدىغان قانۇنىي ھۆججەتلەرنى ئەستايىدىل ئوقۇپ چىقىڭ.',
    terms: 'ئىشلەتكۈچى كېلىشىمى',
    privacy: 'شەخسىي مەخپىيەتلىك سىياسىتى',
    risk: 'قىممەتلىك مېتال خەۋپىنى ئاشكارىلاش',
    loading: 'ھۆججەت يۈكلىنىۋاتىدۇ…',
    error: 'ھۆججەتنى ھازىرچە يۈكلىگىلى بولمىدى. كېيىن قايتا سىناڭ.',
  },
};

function resolveLegalContentLanguage(language?: string | null): LegalContentLanguage {
  const normalized = (language || '').toLowerCase();
  if (normalized === 'hk' || normalized.startsWith('zh-hant') || normalized.startsWith('zh-hk')) return 'hk';
  if (normalized === 'cn' || normalized.startsWith('zh-hans') || normalized.startsWith('zh-cn')) return 'cn';
  if (normalized === 'ug' || normalized.startsWith('ug-')) return 'ug';
  return 'en';
}

function buildPublicAssetUrl(relativePath: string) {
  const base = import.meta.env.BASE_URL || '/';
  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
  return `${normalizedBase}${relativePath.replace(/^\/+/, '')}`;
}

export default function LegalDocumentPage({ documentType }: LegalDocumentPageProps) {
  const { t, i18n } = useTranslation();
  const siteLanguage = i18n.resolvedLanguage || i18n.language;
  const contentLanguage = resolveLegalContentLanguage(siteLanguage);
  const fallbackCopy = PAGE_COPY[contentLanguage];
  const copy = Object.fromEntries(Object.entries(fallbackCopy).map(([key, value]) => [
    key, t(`legal.${key}`, { defaultValue: value }),
  ])) as typeof fallbackCopy;
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const documentUrl = useMemo(
    () => buildPublicAssetUrl(`legal/${contentLanguage === 'ug' ? 'en' : contentLanguage}/${DOCUMENT_FILES[documentType]}`),
    [contentLanguage, documentType],
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    setHtml('');

    fetch(documentUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((content) => {
        setHtml(content);
        setLoading(false);
      })
      .catch((requestError) => {
        if (controller.signal.aborted) return;
        console.error('[JL金利寶] legal document load failed:', requestError);
        setError(true);
        setLoading(false);
      });

    return () => controller.abort();
  }, [documentUrl]);

  const tabs: Array<{ type: LegalDocumentType; label: string }> = [
    { type: 'terms', label: copy.terms },
    { type: 'privacy', label: copy.privacy },
    { type: 'risk', label: copy.risk },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main>
        <section className="relative overflow-hidden bg-gray-950 pb-16 pt-32 text-white md:pb-20 md:pt-40">
          <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-950 to-orange-950/40" />
          <div className="absolute -right-24 top-12 h-80 w-80 rounded-full bg-orange-500/10 blur-3xl" />
          <div className="container relative mx-auto px-4 text-center md:px-6">
            <span className="inline-flex items-center gap-2 rounded-full border border-orange-300/30 bg-orange-400/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-orange-200">
              <i className="ri-file-shield-2-line" />
              {copy.eyebrow}
            </span>
            <h1 className="mt-6 text-3xl font-bold md:text-5xl">{copy.heading}</h1>
            <p className="mx-auto mt-4 max-w-3xl text-sm leading-7 text-gray-300 md:text-lg">
              {copy.subtitle}
            </p>
          </div>
        </section>

        <section className="py-8 md:py-12">
          <div className="container mx-auto px-4 md:px-6">
            <div className="mx-auto mb-6 grid max-w-5xl grid-cols-1 gap-2 rounded-2xl border border-gray-200 bg-white p-2 shadow-sm sm:grid-cols-3">
              {tabs.map((tab) => {
                const active = tab.type === documentType;
                return (
                  <Link
                    key={tab.type}
                    to={DOCUMENT_ROUTES[tab.type]}
                    className={`rounded-xl px-4 py-3 text-center text-sm font-semibold transition-colors ${
                      active
                        ? 'bg-orange-500 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-orange-50 hover:text-orange-700'
                    }`}
                  >
                    {tab.label}
                  </Link>
                );
              })}
            </div>


            <article className="mx-auto max-w-5xl rounded-2xl border border-gray-200 bg-white px-5 py-7 shadow-sm sm:px-8 md:px-12 md:py-12">
              {siteLanguage === 'vn' && (
                <p className="mb-6 text-sm text-gray-500">{t('legal.originalLanguage')}</p>
              )}
              {loading && (
                <div className="flex min-h-48 items-center justify-center text-gray-500">
                  <i className="ri-loader-4-line mr-2 animate-spin text-xl text-orange-500" />
                  {copy.loading}
                </div>
              )}

              {!loading && error && (
                <div className="flex min-h-48 items-center justify-center text-center text-red-600">
                  <div>
                    <i className="ri-error-warning-line mb-2 block text-3xl" />
                    {copy.error}
                  </div>
                </div>
              )}

              {!loading && !error && (
                <div
                  className="legal-document-content"
                  lang={contentLanguage === 'hk' ? 'zh-Hant' : contentLanguage === 'cn' ? 'zh-Hans' : 'en'}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              )}
            </article>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
